import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../scripts/AuthContext";
import { useOrgFilter } from "../scripts/OrgFilterContext";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Users, UserPlus, Crown, MessageSquare, Settings } from "lucide-react";
import { getFullName } from "@/lib/userUtils";
import { toastError, toastMessage } from "@/lib/toast";
import { useConfirm } from "@/components/ConfirmDialogProvider";
import InviteUserDialog from "./InviteUserDialog";
import CreateTeamWizard from "./CreateTeamWizard";
import ManageOrgMemberDialog from "./ManageOrgMemberDialog";
import EditMemberTitleDialog from "./EditMemberTitleDialog";
import { permalinkForPost } from "@/lib/permalinks";
import { useDmDrawer } from "../scripts/DmDrawerContext";
import { getOrCreateDm } from "@/lib/dmApi";
import OrganizationAvatar from "@/components/avatars/OrganizationAvatar";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import MemberProfileHover from "@/components/MemberProfileHover";
import { clearMemberAvatar } from "@/lib/avatarApi";

import { API_BASE_URL } from "@/lib/apiBase";

/** Map legacy tab=feed/posts → teams; teams | members. */
const normalizeOrgTab = (tab) => {
  if (tab === "feed" || tab === "posts") return "teams";
  if (tab === "archive" || tab === "settings") return null;
  if (tab === "teams" || tab === "members") return tab;
  return null;
};

const legacySettingsSection = (raw) => {
  if (raw === "files" || raw === "archive" || raw === "retention") return raw;
  if (raw === "plan") return "plan";
  return "general";
};

const TEAM_RESTRICTED_TOOLTIP =
  "Restricted teams require an invitation to join (or a request-to-join when searchable). They are not followable.";

const LEADERSHIP_GRANTS = ["org.invite", "org.post"];

function isLeadershipGrants(grants) {
  if (!Array.isArray(grants) || grants.length !== LEADERSHIP_GRANTS.length) {
    return false;
  }
  const set = new Set(grants);
  return LEADERSHIP_GRANTS.every((k) => set.has(k));
}

/** Client fallback when API omits badge. Invite + post only for admin/owner viewers. */
function resolveOrgMemberBadge(member, { revealInvitePost = false } = {}) {
  if (member?.badge === "invite_post" && !revealInvitePost) return "member";
  if (member?.badge === "leadership" && !revealInvitePost) return "member";
  if (member?.badge === "leadership") return "invite_post";
  if (member?.badge) return member.badge;
  if (member?.is_owner) return "owner";
  if (member?.role === "admin") return "admin";
  if (revealInvitePost && isLeadershipGrants(member?.grants)) return "invite_post";
  return "member";
}

function badgeLabel(badge) {
  switch (badge) {
    case "owner":
      return "Owner";
    case "admin":
      return "Admin";
    case "invite_post":
      return "Invite + post";
    default:
      return "Member";
  }
}

function badgeVariant(badge) {
  if (badge === "owner") return "default";
  if (badge === "admin") return "secondary";
  if (badge === "invite_post") return "secondary";
  return "outline";
}

const myTeamsToggleStorageKey = (orgId) => `orgShowMyTeamsOnly:${orgId}`;

const loadShowMyTeamsOnly = (orgId) => {
  try {
    const saved = localStorage.getItem(myTeamsToggleStorageKey(orgId));
    if (saved === null) return true;
    return saved === "true";
  } catch {
    return true;
  }
};

const OrgDetail = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { accessToken } = useAuth();
  const confirm = useConfirm();
  const { setSelectedOrgId } = useOrgFilter();
  const { openConversation } = useDmDrawer();
  const [messagingUserId, setMessagingUserId] = useState(null);
  const [org, setOrg] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    return normalizeOrgTab(searchParams.get("tab")) || "teams";
  });
  const [teamsLoadingStats, setTeamsLoadingStats] = useState(false);
  const [showMyTeamsOnly, setShowMyTeamsOnly] = useState(() =>
    loadShowMyTeamsOnly(orgId)
  );
  const [memberTeamIds, setMemberTeamIds] = useState(() => new Set());
  const [memberRolesByTeamId, setMemberRolesByTeamId] = useState(() => ({}));
  const [followerTeamIds, setFollowerTeamIds] = useState(() => new Set());
  const userTeamIds = useMemo(() => {
    const combined = new Set(memberTeamIds);
    followerTeamIds.forEach((id) => combined.add(id));
    return combined;
  }, [memberTeamIds, followerTeamIds]);
  const [showCreateTeamDialog, setShowCreateTeamDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [myCaps, setMyCaps] = useState({
    role: "member",
    is_owner: false,
    capabilities: [],
  });
  const [showEditTitleDialog, setShowEditTitleDialog] = useState(false);
  const [selectedMemberForEdit, setSelectedMemberForEdit] = useState(null);
  const [manageMemberTarget, setManageMemberTarget] = useState(null);
  const [followingTeamId, setFollowingTeamId] = useState(null);
  const [leaveBusy, setLeaveBusy] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [teamSearch, setTeamSearch] = useState("");
  const [selectedMemberIds, setSelectedMemberIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  axios.defaults.withCredentials = true;

  // Sync workspace from the URL when this org route mounts / changes.
  // Do not depend on selectedOrgId — otherwise WorkspaceSwitcher updates are
  // overwritten while this page is still mounted (switch appears to need two clicks).
  useEffect(() => {
    if (orgId) {
      setSelectedOrgId(orgId);
    }
    setDescriptionExpanded(false);
  }, [orgId, setSelectedOrgId]);

  const handleMessageMember = async (member) => {
    const peer = member?.user;
    if (!peer?.user_id || !accessToken || !orgId) return;
    setMessagingUserId(peer.user_id);
    try {
      const conv = await getOrCreateDm(accessToken, orgId, peer.user_id);
      const peerProfile = conv.peer || peer;
      const isDesktop =
        typeof window !== "undefined" &&
        window.matchMedia("(min-width: 768px)").matches;
      if (isDesktop) {
        openConversation(conv.conversation_id, { peer: peerProfile });
      } else {
        navigate(
          `/o/${encodeURIComponent(orgId)}/messages/${conv.conversation_id}`
        );
      }
    } catch (err) {
      console.error("Failed to open DM:", err);
      toastError(err, "Could not open conversation");
    } finally {
      setMessagingUserId(null);
    }
  };

  // Fetch current user (axios interceptor refreshes expired tokens)
  useEffect(() => {
    if (!accessToken) return;

    const fetchCurrentUser = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/user/me`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        setCurrentUser(response.data);
      } catch (error) {
        console.error("Failed to fetch current user:", error);
      }
    };

    fetchCurrentUser();
  }, [accessToken]);

  // Interceptors handle token refresh; this just runs the request with the current token
  const makeAuthenticatedRequest = async (requestFn, token = accessToken) => {
    return requestFn(token);
  };

  // Fetch organization details
  const fetchOrgDetails = async (token) => {
    const response = await axios.get(`${API_BASE_URL}/org/${orgId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  // Fetch teams visible to the current user:
  // organization-wide + restricted searchable (requestable), plus any teams they belong to
  // (covers restricted / non-searchable teams like Leadership).
  const fetchTeams = async (token) => {
    const [organizationResponse, requestableResponse, membershipsResponse] =
      await Promise.all([
        axios.get(`${API_BASE_URL}/team/organization`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { org_id: orgId },
        }),
        axios.get(`${API_BASE_URL}/team/requestable`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { org_id: orgId },
        }),
        axios.get(`${API_BASE_URL}/team/memberships`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { org_id: orgId },
        }),
      ]);

    const allTeams = [
      ...(organizationResponse.data || []),
      ...(requestableResponse.data || []),
      ...(membershipsResponse.data || []),
    ];
    const uniqueTeams = Array.from(
      new Map(
        allTeams
          .map((team) => {
            const id = team.team_id ?? team.teamId;
            return id != null ? [id, { ...team, team_id: id }] : null;
          })
          .filter(Boolean)
      ).values()
    );
    return uniqueTeams;
  };

  // Fetch user's teams (memberships and follows) as separate sets
  const fetchUserTeams = async (token) => {
    try {
      const membershipsResponse = await axios.get(`${API_BASE_URL}/team/memberships`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { org_id: orgId },
      });

      const followsResponse = await axios.get(`${API_BASE_URL}/team/follows`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { org_id: orgId },
      });

      const memberships = membershipsResponse.data || [];
      return {
        memberIds: new Set(
          memberships.map((t) => t.team_id ?? t.teamId).filter((id) => id != null)
        ),
        memberRoles: Object.fromEntries(
          memberships.map((t) => [t.team_id ?? t.teamId, t.role])
        ),
        followIds: new Set(
          (followsResponse.data || [])
            .map((t) => t.team_id ?? t.teamId)
            .filter((id) => id != null)
        ),
      };
    } catch (err) {
      console.error("Failed to fetch user teams:", err);
      return { memberIds: new Set(), memberRoles: {}, followIds: new Set() };
    }
  };

  const applyUserTeamIds = (result) => {
    setMemberTeamIds(result?.memberIds || new Set());
    setMemberRolesByTeamId(result?.memberRoles || {});
    setFollowerTeamIds(result?.followIds || new Set());
  };

  // Fetch org members
  const fetchOrgMembers = async () => {
    if (!accessToken || !orgId) return;

    setMembersLoading(true);
    try {
      const response = await makeAuthenticatedRequest(async (token) => {
        return await axios.get(`${API_BASE_URL}/org/${orgId}/members`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      });

      setMembers(response.data || []);
    } catch (err) {
      console.error("Failed to fetch org members:", err);
    } finally {
      setMembersLoading(false);
    }
  };

  // Enrich teams with statistics from list payload (memberCount/followerCount/admins from API)
  const enrichTeamsWithStats = async (teamsList, token) => {
    if (!teamsList || teamsList.length === 0) {
      return teamsList;
    }

    setTeamsLoadingStats(true);
    try {
      return teamsList.map((team) => {
        const memberCount =
          team.memberCount ??
          team.member_count ??
          team.stats?.memberCount ??
          0;
        const followerCount =
          team.followerCount ??
          team.follower_count ??
          team.stats?.followerCount ??
          0;
        const admins = Array.isArray(team.admins)
          ? team.admins
          : Array.isArray(team.stats?.admins)
            ? team.stats.admins
            : [];

        return {
          ...team,
          stats: {
            memberCount,
            followerCount,
            admins,
          },
        };
      });
    } catch (err) {
      console.error("Failed to enrich teams with stats:", err);
      return teamsList.map((team) => ({
        ...team,
        stats: {
          memberCount: team.memberCount ?? team.member_count ?? 0,
          followerCount: team.followerCount ?? team.follower_count ?? 0,
          admins: Array.isArray(team.admins) ? team.admins : [],
        },
      }));
    } finally {
      setTeamsLoadingStats(false);
    }
  };

  // Persist "Show only my teams" across team navigation / remounts
  useEffect(() => {
    setShowMyTeamsOnly(loadShowMyTeamsOnly(orgId));
  }, [orgId]);

  const handleToggleShowMyTeamsOnly = () => {
    setShowMyTeamsOnly((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(myTeamsToggleStorageKey(orgId), String(next));
      } catch (err) {
        console.error("Failed to save my-teams toggle:", err);
      }
      return next;
    });
  };

  const handleFollowFromBrowse = async (team, event) => {
    event?.stopPropagation?.();
    const teamId = team.team_id ?? team.teamId;
    if (!orgId || teamId == null || !accessToken) return;
    if (team.visibility === "restricted") {
      toastError("Restricted teams cannot be followed. Request to join or ask for an invite.");
      return;
    }
    if (memberTeamIds.has(teamId) || followerTeamIds.has(teamId)) return;

    setFollowingTeamId(teamId);
    try {
      await makeAuthenticatedRequest(async (token) => {
        await axios.post(
          `${API_BASE_URL}/team/follow`,
          { team_id: teamId, org_id: orgId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      });

      setFollowerTeamIds((prev) => {
        const next = new Set(prev);
        next.add(teamId);
        return next;
      });
      setTeams((prev) =>
        prev.map((t) => {
          const id = t.team_id ?? t.teamId;
          if (id !== teamId) return t;
          const prevCount = t.stats?.followerCount ?? t.followerCount ?? 0;
          const nextCount = Number(prevCount) + 1;
          return {
            ...t,
            followerCount: nextCount,
            follower_count: nextCount,
            stats: {
              ...(t.stats || {}),
              followerCount: nextCount,
            },
          };
        })
      );
    } catch (err) {
      console.error("Failed to follow team:", err);
      toastError(err, "Failed to follow team");
    } finally {
      setFollowingTeamId(null);
    }
  };

  const isOrgOwner =
    !!currentUser && !!org?.owned_by && currentUser.user_id === org.owned_by;

  // Filter + sort teams: memberships/follows first, then search by name/description
  const filteredTeams = useMemo(() => {
    const mineOnly = !(isOrgOwner || myCaps.is_owner) && showMyTeamsOnly;
    const q = teamSearch.trim().toLowerCase();

    const list = teams.filter((team) => {
      const id = team.team_id ?? team.teamId;
      if (mineOnly && !userTeamIds.has(id)) return false;
      if (!q) return true;
      const haystack = [team.name, team.description, team.visibility]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });

    const rank = (team) => {
      const id = team.team_id ?? team.teamId;
      if (memberTeamIds.has(id)) return 0;
      if (followerTeamIds.has(id)) return 1;
      return 2;
    };

    return [...list].sort((a, b) => {
      const rankDiff = rank(a) - rank(b);
      if (rankDiff !== 0) return rankDiff;
      return String(a.name || "").localeCompare(String(b.name || ""), undefined, {
        sensitivity: "base",
      });
    });
  }, [
    teams,
    userTeamIds,
    memberTeamIds,
    followerTeamIds,
    showMyTeamsOnly,
    isOrgOwner,
    myCaps.is_owner,
    teamSearch,
  ]);

  const filteredMembers = useMemo(() => {
    const revealInvitePost =
      isOrgOwner || myCaps.is_owner || myCaps.role === "admin";
    const q = memberSearch.trim().toLowerCase();
    if (!q) return members;
    return members.filter((member) => {
      const user = member.user || {};
      const badge = resolveOrgMemberBadge(member, { revealInvitePost });
      const haystack = [
        user.first_name,
        user.last_name,
        member.org_email,
        member.title,
        member.role,
        badge,
        badgeLabel(badge),
        ...(member.team_admin_of || []).map((t) => t.name),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [members, memberSearch, isOrgOwner, myCaps.is_owner, myCaps.role]);

  // Handle create team
  const handleCreateTeam = () => {
    setShowCreateTeamDialog(true);
  };

  useEffect(() => {
    const onTourCreateTeam = (e) => {
      if (String(e.detail?.orgId) !== String(orgId)) return;
      setShowCreateTeamDialog(true);
    };
    window.addEventListener("circa-tour-open-create-team", onTourCreateTeam);
    return () =>
      window.removeEventListener(
        "circa-tour-open-create-team",
        onTourCreateTeam
      );
  }, [orgId]);

  const handleCreateTeamFinished = async () => {
    const teamsData = await makeAuthenticatedRequest(fetchTeams);
    const enrichedTeams = await enrichTeamsWithStats(teamsData, accessToken);
    setTeams(enrichedTeams);
    const userTeams = await makeAuthenticatedRequest(fetchUserTeams);
    applyUserTeamIds(userTeams);
  };

  // Load all data
  useEffect(() => {
    if (!accessToken || !orgId) return;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch org details
        const orgData = await makeAuthenticatedRequest(fetchOrgDetails);
        setOrg({
          id: orgData.org_id,
          org_id: orgData.org_id,
          name: orgData.name,
          description: orgData.description || "",
          created_at: orgData.created_at,
          owned_by: orgData.owned_by,
          avatar_url: orgData.avatar_url || null,
          brand_primary: orgData.brand_primary || null,
          brand_secondary: orgData.brand_secondary || null,
          brand_accent: orgData.brand_accent || null,
        });

        try {
          const meRes = await axios.get(`${API_BASE_URL}/org/${orgId}/me`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          setMyCaps({
            role: meRes.data?.role || "member",
            is_owner: Boolean(meRes.data?.is_owner),
            capabilities: Array.isArray(meRes.data?.capabilities)
              ? meRes.data.capabilities
              : [],
          });
        } catch (meErr) {
          console.error("Failed to load org capabilities:", meErr);
        }

        // Fetch teams
        const teamsData = await makeAuthenticatedRequest(fetchTeams);
        
        // Enrich teams with statistics
        const enrichedTeams = await enrichTeamsWithStats(teamsData, accessToken);
        setTeams(enrichedTeams);

        // Fetch user's teams (memberships and follows)
        const userTeams = await makeAuthenticatedRequest(fetchUserTeams);
        applyUserTeamIds(userTeams);

        // Prefetch org members so Members(N) is correct before tab click
        try {
          const membersResponse = await makeAuthenticatedRequest(async (token) => {
            return await axios.get(`${API_BASE_URL}/org/${orgId}/members`, {
              headers: { Authorization: `Bearer ${token}` },
            });
          });
          setMembers(membersResponse.data || []);
        } catch (membersErr) {
          console.error("Failed to prefetch org members:", membersErr);
        }

        setLoading(false);
      } catch (err) {
        console.error("Failed to load org data:", err);
        setError(err.message || "Failed to load organization data");
        setLoading(false);
      }
    };

    loadData();
    // Intentionally omit request helpers — reload when org/auth identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, orgId]);

  // Legacy tab=feed/posts → teams; tab=settings|archive → /orgs/:id/settings
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl === "feed" || tabFromUrl === "posts") {
      const next = new URLSearchParams(searchParams);
      next.set("tab", "teams");
      setSearchParams(next, { replace: true });
      setActiveTab("teams");
      return;
    }
    if (tabFromUrl === "settings" || tabFromUrl === "archive") {
      const section =
        tabFromUrl === "archive"
          ? "archive"
          : legacySettingsSection(searchParams.get("settings"));
      const qs = section && section !== "general" ? `?section=${section}` : "";
      navigate(`/orgs/${orgId}/settings${qs}`, { replace: true });
      return;
    }
    const normalized = normalizeOrgTab(tabFromUrl);
    if (normalized && normalized !== activeTab) {
      setActiveTab(normalized);
    }
  }, [searchParams, orgId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Legacy ?post= → canonical permalink (post detail routes still exist)
  useEffect(() => {
    const postIdFromUrl = searchParams.get("post");
    if (!postIdFromUrl || !orgId) return;
    const params = new URLSearchParams();
    if (searchParams.get("from") === "catchup") {
      params.set("from", "catchup");
    }
    const returnTo = searchParams.get("return");
    if (returnTo) params.set("return", returnTo);
    const qs = params.toString();
    navigate(
      `${permalinkForPost({ orgId, postId: postIdFromUrl })}${qs ? `?${qs}` : ""}`,
      { replace: true }
    );
  }, [searchParams, orgId, navigate]);

  // Fetch members when members tab becomes active
  useEffect(() => {
    if (activeTab === "members" && orgId && accessToken) {
      fetchOrgMembers();
    }
    // Intentionally omit fetchOrgMembers — load when tab/org/auth changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, orgId, accessToken]);

  const caps = new Set(myCaps.capabilities || []);
  const canInvite = caps.has("org.invite") || myCaps.is_owner || isOrgOwner;
  const canCreateTeams = caps.has("org.create_teams") || myCaps.is_owner || isOrgOwner;
  const canManageSettings =
    caps.has("org.manage_settings") || myCaps.is_owner || isOrgOwner;
  const canIntegrations =
    caps.has("org.integrations") || myCaps.is_owner || isOrgOwner;
  const canOpenSettings = canManageSettings || canIntegrations;
  const canRemoveMembers =
    caps.has("org.remove_members") || myCaps.is_owner || isOrgOwner;
  const canManageRoles =
    myCaps.is_owner || myCaps.role === "admin" || isOrgOwner;

  const refreshMyCaps = async () => {
    if (!accessToken || !orgId) return;
    try {
      const meRes = await axios.get(`${API_BASE_URL}/org/${orgId}/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setMyCaps({
        role: meRes.data?.role || "member",
        is_owner: Boolean(meRes.data?.is_owner),
        capabilities: Array.isArray(meRes.data?.capabilities)
          ? meRes.data.capabilities
          : [],
      });
    } catch (err) {
      console.error("Failed to refresh org capabilities:", err);
    }
  };

  const refreshMembers = async () => {
    await fetchOrgMembers();
    await refreshMyCaps();
    setSelectedMemberIds(new Set());
  };

  const toggleMemberSelected = (userId) => {
    if (!userId) return;
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleBulkPreset = async (preset) => {
    if (!accessToken || !orgId || selectedMemberIds.size === 0 || !canManageRoles) {
      return;
    }
    setBulkBusy(true);
    try {
      const result = await axios.post(
        `${API_BASE_URL}/org/${orgId}/members/bulk`,
        {
          user_ids: [...selectedMemberIds],
          preset,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const skipped = result.data?.skipped || [];
      if (skipped.length > 0) {
        const reasons = skipped
          .map((s) => s.reason || "skipped")
          .filter(Boolean);
        toastMessage(`Updated ${result.data?.updated?.length || 0}. Skipped ${skipped.length}: ${reasons[0] || "see details"}`);
      }
      await refreshMembers();
    } catch (err) {
      toastError(err, "Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const handleLeaveOrg = async () => {
    if (!accessToken || !orgId || isOrgOwner || myCaps.is_owner) return;
    const ok = await confirm({
      title: "Leave organization?",
      description: "Are you sure you want to leave this organization?",
      confirmLabel: "Leave",
      variant: "destructive",
    });
    if (!ok) return;
    setLeaveBusy(true);
    try {
      await axios.delete(`${API_BASE_URL}/org/${orgId}/membership`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      navigate("/dashboard");
    } catch (err) {
      toastError(err, "Failed to leave");
    } finally {
      setLeaveBusy(false);
    }
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    const next = new URLSearchParams(searchParams);
    next.set("tab", value);
    next.delete("settings");
    setSearchParams(next);
  };

  if (loading) {
    return (
      <div className="flex flex-col bg-background text-foreground min-h-screen">
        <div className="flex-1 p-6 flex items-center justify-center">
          <div className="text-muted-foreground">Loading organization...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col bg-background text-foreground min-h-screen">
        <div className="flex-1 p-6">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button onClick={() => navigate("/dashboard")} className="mt-4">
            Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="flex flex-col bg-background text-foreground min-h-screen">
        <div className="flex-1 p-6">
          <div className="text-muted-foreground">Organization not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-background text-foreground min-h-0 flex-1">
      <header className="hidden md:flex items-center gap-4 border-b border-border p-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard")}
        >
          Back to dashboard
        </Button>
      </header>
      <div className="flex-1 px-4 py-3 md:p-6 overflow-y-auto min-w-0">

        {/* Organization Header */}
        <div className="mb-6">
          <div className="flex items-start gap-4 mb-4">
            <OrganizationAvatar
              org={org}
              className="h-16 w-16"
              fallbackClassName="text-lg"
            />
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold md:text-3xl break-words">{org.name}</h1>
                {canOpenSettings && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground"
                          aria-label="Organization settings"
                          data-tour="org-settings"
                          onClick={() =>
                            navigate(
                              canManageSettings
                                ? `/orgs/${orgId}/settings`
                                : `/orgs/${orgId}/settings?section=connectors`
                            )
                          }
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Organization settings</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {org.description ? (
                <div className="space-y-1">
                  <p
                    className={
                      descriptionExpanded
                        ? "text-muted-foreground"
                        : "text-muted-foreground line-clamp-2 md:line-clamp-none"
                    }
                  >
                    {org.description}
                  </p>
                  <button
                    type="button"
                    className="text-sm font-medium text-primary hover:underline md:hidden"
                    onClick={() => setDescriptionExpanded((v) => !v)}
                  >
                    {descriptionExpanded ? "Show less" : "Show more"}
                  </button>
                </div>
              ) : (
                <p className="hidden text-muted-foreground md:block">
                  No description
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm flex-wrap">
            <span className="text-muted-foreground">
              Created: {new Date(org.created_at).toLocaleDateString()}
            </span>
            {(myCaps.role === "admin" || myCaps.is_owner) && (
              <Badge variant="secondary">
                {myCaps.is_owner ? "Owner" : "Admin"}
              </Badge>
            )}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="max-w-full h-auto flex-wrap">
            <TabsTrigger value="teams">Teams ({filteredTeams.length})</TabsTrigger>
            <TabsTrigger value="members">Members ({members.length})</TabsTrigger>
          </TabsList>

          {/* Teams Tab */}
          <TabsContent value="teams">
            <div className="mb-4 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  {canCreateTeams && (
                    <Button
                      data-tour="create-team"
                      onClick={handleCreateTeam}
                    >
                      + Create Team
                    </Button>
                  )}
                  {canInvite && (
                    <Button onClick={() => setShowInviteDialog(true)} variant="outline">
                      Invite people
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {(isOrgOwner || myCaps.is_owner) ? (
                    <span className="text-sm text-muted-foreground">
                      Showing all teams
                    </span>
                  ) : (
                    <>
                      <label htmlFor="show-my-teams-toggle" className="text-sm text-muted-foreground cursor-pointer">
                        Show only my teams
                      </label>
                      <Button
                        id="show-my-teams-toggle"
                        variant={showMyTeamsOnly ? "default" : "outline"}
                        size="sm"
                        onClick={handleToggleShowMyTeamsOnly}
                        className="h-8"
                      >
                        {showMyTeamsOnly ? "ON" : "OFF"}
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div className="max-w-md">
                <Label htmlFor="team-search" className="sr-only">
                  Search teams
                </Label>
                <Input
                  id="team-search"
                  type="search"
                  value={teamSearch}
                  onChange={(e) => setTeamSearch(e.target.value)}
                  placeholder="Search teams by name or description…"
                />
              </div>
            </div>
            {filteredTeams.length === 0 ? (
              <p className="text-muted-foreground">
                {teamSearch.trim()
                  ? `No teams match “${teamSearch.trim()}”.`
                  : showMyTeamsOnly
                    ? "No teams found that you're a member of or following."
                    : "No teams found in this organization."}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTeams.map((team) => {
                  const teamId = team.team_id ?? team.teamId;
                  const isMember = memberTeamIds.has(teamId);
                  const isFollower = followerTeamIds.has(teamId);
                  const canFollow =
                    !isMember &&
                    !isFollower &&
                    team.visibility === "organization";
                  const isFollowPending = followingTeamId === teamId;

                  return (
                  <Card 
                    key={teamId} 
                    className="hover:shadow-lg transition-all cursor-pointer"
                    onClick={() => navigate(`/teams/${teamId}?org_id=${orgId}`)}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-xl">{team.name}</CardTitle>
                        <div className="flex flex-wrap gap-1 justify-end shrink-0">
                          {isMember ? (
                            <Badge className="bg-primary/15 text-primary hover:bg-primary/15 border border-primary/30">
                              {memberRolesByTeamId[teamId] === "admin"
                                ? "Admin"
                                : "Member"}
                            </Badge>
                          ) : isFollower ? (
                            <Badge variant="secondary">Following</Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">
                              Not joined
                            </Badge>
                          )}
                        </div>
                      </div>
                      <CardDescription>{team.description || "No description"}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-xs space-y-2">
                        {team.visibility === "restricted" && (
                          <div className="flex items-center gap-2 flex-wrap">
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className="inline-flex cursor-help"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Badge variant="destructive">Restricted</Badge>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs text-sm leading-snug">
                                  {TEAM_RESTRICTED_TOOLTIP}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        )}
                        
                        {/* Team Statistics */}
                        <div className="space-y-1.5 pt-1 border-t border-border">
                          {/* Member Count */}
                          <div className="flex items-center gap-2">
                            <Users className="h-3 w-3 text-muted-foreground" />
                            <span className="text-muted-foreground">
                              {team.stats?.memberCount !== null && team.stats?.memberCount !== undefined
                                ? `${team.stats.memberCount} ${team.stats.memberCount === 1 ? 'member' : 'members'}`
                                : teamsLoadingStats ? 'Loading...' : '— members'}
                            </span>
                          </div>
                          
                          {/* Follower Count */}
                          <div className="flex items-center gap-2">
                            <UserPlus className="h-3 w-3 text-muted-foreground" />
                            <span className="text-muted-foreground">
                              {team.stats?.followerCount !== null && team.stats?.followerCount !== undefined
                                ? `${team.stats.followerCount} ${team.stats.followerCount === 1 ? 'follower' : 'followers'}`
                                : teamsLoadingStats ? 'Loading...' : '— followers'}
                            </span>
                          </div>
                          
                          {/* Team Admins */}
                          <div className="flex items-start gap-2">
                            <Crown className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                            <span className="text-muted-foreground">
                              {(() => {
                                const admins =
                                  team.stats?.admins ??
                                  team.admins ??
                                  [];
                                if (teamsLoadingStats && !admins.length) {
                                  return "Loading admins…";
                                }
                                if (!admins.length) {
                                  return "Admins: —";
                                }
                                const names = admins
                                  .map((a) => getFullName(a) || "Unknown")
                                  .filter(Boolean);
                                return `Admins: ${names.join(", ")}`;
                              })()}
                            </span>
                          </div>
                        </div>

                        {canFollow && (
                          <div className="pt-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="w-full"
                              disabled={isFollowPending}
                              onClick={(e) => handleFollowFromBrowse(team, e)}
                            >
                              {isFollowPending ? "Following…" : "Follow"}
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members" className="mt-4">
            {membersLoading ? (
              <p className="text-muted-foreground">Loading members...</p>
            ) : members.length === 0 ? (
              <p className="text-muted-foreground">No members found.</p>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="max-w-md flex-1">
                    <Label htmlFor="member-search" className="sr-only">
                      Search members
                    </Label>
                    <Input
                      id="member-search"
                      type="search"
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      placeholder="Search by name, email, title, or role…"
                    />
                  </div>
                  {canManageRoles && selectedMemberIds.size > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        {selectedMemberIds.size} selected
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={bulkBusy}
                        onClick={() => handleBulkPreset("leadership")}
                      >
                        Set as Invite + post
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={bulkBusy}
                        onClick={() => handleBulkPreset("member")}
                      >
                        Clear to Member
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={bulkBusy}
                        onClick={() => handleBulkPreset("admin")}
                      >
                        Make Admin
                      </Button>
                    </div>
                  )}
                </div>
                {filteredMembers.length === 0 ? (
                  <p className="text-muted-foreground">
                    No members match “{memberSearch.trim()}”.
                  </p>
                ) : (
                  filteredMembers.map((member) => {
                  const isCurrentUser = currentUser && member.user.user_id === currentUser.user_id;
                  const badge = resolveOrgMemberBadge(member, {
                    revealInvitePost: canManageRoles,
                  });
                  const teamAdminNames = (member.team_admin_of || [])
                    .map((t) => t.name)
                    .filter(Boolean);
                  const userId = member.user?.user_id;
                  const profileUser = member.user
                    ? {
                        ...member.user,
                        title: member.title || member.user.title || null,
                        org_email:
                          member.org_email ?? member.user.org_email ?? null,
                        member_avatar_url:
                          member.member_avatar_url ||
                          member.user.member_avatar_url ||
                          null,
                      }
                    : null;
                  const selectable =
                    canManageRoles && !member.is_owner && !isCurrentUser && userId;
                  return (
                    <Card key={member.membership_id}>
                      <CardContent className="flex items-center gap-4 p-4">
                        {canManageRoles && (
                          <input
                            type="checkbox"
                            className="h-4 w-4 shrink-0"
                            disabled={!selectable || bulkBusy}
                            checked={selectable && selectedMemberIds.has(userId)}
                            onChange={() => toggleMemberSelected(userId)}
                            aria-label={`Select ${member.user?.first_name || "member"}`}
                          />
                        )}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <MemberProfileHover
                            user={profileUser}
                            orgId={orgId}
                          >
                            <OrgMemberAvatar
                              user={profileUser}
                              memberAvatarUrl={
                                member.member_avatar_url ||
                                member.user?.member_avatar_url
                              }
                            />
                          </MemberProfileHover>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">
                              <MemberProfileHover
                                user={profileUser}
                                orgId={orgId}
                              >
                                <span className="hover:underline">
                                  {member.user.first_name}{" "}
                                  {member.user.last_name}
                                </span>
                              </MemberProfileHover>
                            </p>
                            {member.org_email ? (
                              <p className="text-sm text-muted-foreground">{member.org_email}</p>
                            ) : null}
                            <div className="flex flex-wrap gap-1 mt-1">
                              <Badge variant={badgeVariant(badge)}>
                                {badgeLabel(badge)}
                              </Badge>
                            </div>
                            {member.title ? (
                              <p className="text-sm text-muted-foreground mt-1">{member.title}</p>
                            ) : (
                              <p className="text-sm text-muted-foreground italic mt-1">No title</p>
                            )}
                            {canManageRoles && teamAdminNames.length > 0 && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Admin on {teamAdminNames.join(", ")}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 flex-wrap justify-end">
                          <span className="text-xs text-muted-foreground">
                            Joined: {new Date(member.joined_at).toLocaleDateString()}
                          </span>
                          {!isCurrentUser && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={messagingUserId === member.user.user_id}
                              onClick={() => handleMessageMember(member)}
                            >
                              <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                              Message
                            </Button>
                          )}
                          {!isCurrentUser &&
                            (canManageRoles || canRemoveMembers || isOrgOwner) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setManageMemberTarget(member)}
                              >
                                Manage
                              </Button>
                            )}
                          {!isCurrentUser &&
                            isOrgOwner &&
                            (member.member_avatar_url ||
                              member.user?.member_avatar_url) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    await clearMemberAvatar(
                                      accessToken,
                                      orgId,
                                      member.user.user_id
                                    );
                                    await refreshMembers();
                                  } catch (err) {
                                    toastError(err, "Could not clear avatar");
                                  }
                                }}
                              >
                                Clear photo
                              </Button>
                            )}
                          {isCurrentUser && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedMemberForEdit(member);
                                setShowEditTitleDialog(true);
                              }}
                            >
                              Edit Title
                            </Button>
                          )}
                          {isCurrentUser && !isOrgOwner && !myCaps.is_owner && (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={leaveBusy}
                              onClick={handleLeaveOrg}
                            >
                              Leave org
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
                )}
              </div>
            )}
          </TabsContent>

        </Tabs>
      </div>

      {/* Create Team Wizard */}
      <CreateTeamWizard
        open={showCreateTeamDialog}
        onOpenChange={setShowCreateTeamDialog}
        orgId={orgId}
        currentUserId={currentUser?.user_id}
        onCreated={handleCreateTeamFinished}
        canManageSettings={canManageSettings}
      />

      {/* Invite User Dialog */}
      <InviteUserDialog
        isOpen={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        orgId={orgId}
        onSuccess={() => {
          // Refresh user teams after invite
          const refreshData = async () => {
            try {
              const userTeams = await makeAuthenticatedRequest(fetchUserTeams);
              applyUserTeamIds(userTeams);
            } catch (err) {
              console.error("Failed to refresh user teams:", err);
            }
          };
          refreshData();
        }}
      />

      {/* Edit Member Title Dialog */}
      <EditMemberTitleDialog
        isOpen={showEditTitleDialog}
        onClose={() => {
          setShowEditTitleDialog(false);
          setSelectedMemberForEdit(null);
        }}
        member={selectedMemberForEdit}
        orgId={orgId}
        onSuccess={() => {
          fetchOrgMembers();
        }}
      />

      <ManageOrgMemberDialog
        isOpen={!!manageMemberTarget}
        onClose={() => setManageMemberTarget(null)}
        orgId={orgId}
        accessToken={accessToken}
        member={manageMemberTarget}
        actorIsOwner={isOrgOwner || myCaps.is_owner}
        canManageRoles={canManageRoles}
        canRemoveMembers={canRemoveMembers}
        onSuccess={async () => {
          await refreshMembers();
          const orgData = await makeAuthenticatedRequest(fetchOrgDetails);
          setOrg((prev) =>
            prev
              ? {
                  ...prev,
                  owned_by: orgData.owned_by,
                  name: orgData.name,
                  description: orgData.description || "",
                }
              : prev
          );
        }}
      />
    </div>
  );
};

export default OrgDetail;

